import React, { useState, useContext, useEffect } from "react";
import { Button, Alert, Input } from "reactstrap";
import Select from "react-select";
import ToolkitProvider, {CSVExport} from "react-bootstrap-table2-toolkit";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import ReleaseDropdown from "./releaseDropdown";
import ReleaseStoreUrls from "./releaseStoreUrls";
import QCNote from "./qcNote";
import ACRAlert from "./acrAlert";
import ReleaseEDT from "./releaseEDT";
import ViewLayout from "../components/viewLayout";
import ReleaseSubUserFilter from "./releaseSubUserFilter";
import ListenTracks from "./listenTracks";
import PreScan from "./preScan";
import ReleaseListLayoutOne from "./releaseListLayoutOne";
import ReleaseListLayoutTwo from "./releaseListLayoutTwo";
import {
  CARD_SORT_OPTIONS,
  LAYOUT_OPTIONS,
  QUICK_ACTIONS_LEGEND_ROW_ID,
  RELEASE_STATUS_FILTER_OPTIONS,
  getDisplayStatus,
  hasQCIssues,
  setStoredReleaseListLayout,
} from "./releaseTableHelpers";
import { getDemoTrackBlob, isWlDemoBlobRef, wlDemoBlobStorageKeyFromRef } from "../utils/demoTrackBlobStore";
import { pickArtworkSrc } from "../utils/demoArtworkHelpers";
import "./releaseTable.scss";

import { StateContext } from "../utils/context";
import TenantContext from "../utils/tenantContext";
import { isDemoLocked } from "../utils/demoConfig";

const CARD_SORT_SELECT_OPTIONS = CARD_SORT_OPTIONS.map(({ value, label }) => ({ value, label }));
const STATUS_FILTER_SELECT_OPTIONS = RELEASE_STATUS_FILTER_OPTIONS.map(({ value, label }) => ({ value, label }));

const RELEASE_TOOLBAR_SELECT_LAYOUT = {
  menu: (styles) => ({ ...styles, zIndex: 10 }),
  control: (styles) => ({
    ...styles,
    minHeight: 36,
    height: 36,
    border: "none",
    boxShadow: "none",
    backgroundColor: "transparent",
    cursor: "pointer",
  }),
  valueContainer: (styles) => ({
    ...styles,
    padding: "0 4px 0 0",
    flexWrap: "nowrap",
    height: 34,
    overflow: "hidden",
  }),
  dropdownIndicator: (styles) => ({ ...styles, padding: "6px", color: "#6c757d" }),
  indicatorSeparator: () => ({ display: "none" }),
};

const RELEASE_TOOLBAR_SUBUSER_SELECT_STYLES = {
  ...RELEASE_TOOLBAR_SELECT_LAYOUT,
  input: (styles) => ({
    ...styles,
    fontSize: 14,
    margin: 0,
    padding: 0,
  }),
};

const RELEASE_TOOLBAR_SORT_SELECT_STYLES = {
  ...RELEASE_TOOLBAR_SELECT_LAYOUT,
  singleValue: (styles) => ({
    ...styles,
    color: "#6c757d",
    fontSize: 14,
    maxWidth: "100%",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),
};

const DEFAULT_RELEASE_ORDERING = { order: "-", name: "official_date", sort: "official_date" };

const releaseAvatarImgStyle = {
  width: 40,
  height: 40,
  borderRadius: "100%",
};

const releaseAvatarPlaceholderStyle = {
  ...releaseAvatarImgStyle,
  display: "inline-block",
  margin: "0 10px 0 0",
  flexShrink: 0,
};

/** Async thumbnail only for demo blob refs; normal URLs render like whitelabeltenant. */
const ReleaseAvatarBlob = ({ raw, alt }) => {
  const [src, setSrc] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;

    setLoadFailed(false);
    setSrc(null);

    (async () => {
      try {
        const storageKey = wlDemoBlobStorageKeyFromRef(raw);
        const blob = await getDemoTrackBlob(storageKey);
        if (cancelled || !blob) {
          if (!cancelled) {
            setLoadFailed(true);
          }
          return;
        }
        objectUrl = URL.createObjectURL(blob);
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        setSrc(objectUrl);
      } catch (e) {
        if (!cancelled) {
          setLoadFailed(true);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [raw]);

  if (src) {
    return <img style={releaseAvatarImgStyle} src={src} alt={alt} />;
  }
  if (loadFailed) {
    return null;
  }
  return <span style={releaseAvatarPlaceholderStyle} className="release-avatar-placeholder" aria-hidden="true" />;
};

const ReleaseTable = ({
  tableData,
  defaultSorted,
  forceUpdate,
  setForceUpdate,
  searchQuery,
  subUserData,
  todUrl,
  touUrl,
  ppUrl,
  canAddTakedown,
  layoutView = "table",
  layoutToggleEnabled = false,
  onLayoutChange,
}) => {
  const { currentUser } = useContext(StateContext);
  const { tenant } = useContext(TenantContext);
  const { username, is_premium_user, is_sub_user, is_employee_account } = currentUser;
  const { release_admin_approval, acr_deep_scan } = tenant;
  const effectiveCanAddTakedown =
    canAddTakedown ||
    (isDemoLocked() && currentUser?.can_add_takedown !== false);

  const { ExportCSVButton } = CSVExport;

  const [successAction, setSuccessAction] = useState(false);
  const [declarationSignSuccess, setDeclarationSignSuccess] = useState(false);
  const [declarationSignError, setDeclarationSignError] = useState(false);
  const [preScanSuccess, setPreScanSuccess] = useState(false);
  const [preScanError, setPreScanError] = useState(false);
  const [preScanDeclarationSignSuccess, setPreScanDeclarationSignSuccess] = useState(false);
  const [preScanDeclarationSignError, setPreScanDeclarationSignError] = useState(false);
  const dropdownAction = {
    successAction,
    setSuccessAction,
    forceUpdate,
    setForceUpdate,
  };

  const handleLayoutChange = (nextLayout) => {
    if (!layoutToggleEnabled) {
      return;
    }
    setStoredReleaseListLayout(nextLayout);
    if (onLayoutChange) {
      onLayoutChange(nextLayout);
    }
  };

  const statusFormatter = (_cell, row) => {
    if (isQuickActionsLegendRow(row)) {
      return null;
    }
    const displayStatus = getDisplayStatus(row, { username, is_premium_user, is_employee_account });
    return (
      <>
        {displayStatus === "ready" ? (
          <Button className="btn-status-big" disabled color="info">
            Ready
          </Button>
        ) : displayStatus === "pending" ? (
          <Button className="btn-status-big" disabled color="warning">
            Pending
          </Button>
        ) : displayStatus === "approval" ? (
          <Button className="btn-status-big" disabled color="warning">
            Submission Queued
          </Button>
        ) : displayStatus === "admin_approval" ? (
          <Button className="btn-status-big" disabled color={(release_admin_approval && (is_premium_user || is_employee_account)) ? "info" : "warning"}>
            {(release_admin_approval && (is_premium_user || is_employee_account)) ? "Ready" : "Submission Queued"}
          </Button>
        ) : displayStatus === "takedown_request" ? (
          <Button className="btn-status-big" disabled color="warning">
            Takedown request
          </Button>
        ) : displayStatus === "taken_down" ? (
          <Button className="btn-status-big" disabled color="danger">
            Taken down
          </Button>
        ) : displayStatus === "distributed" ? (
          <Button className="btn-status-big" disabled color="scooter">
            Distributed
          </Button>
        ) : displayStatus === "locked" ? (
          <Button className="btn-status-big" disabled color="info">
            In Progress - Locked
          </Button>
        ) : displayStatus === "delivering" ? (
          <Button className="btn-status-big" disabled color="info">
            Delivering
          </Button>
        ) : displayStatus === "unlocked" ? (
          <Button className="btn-status-big" disabled color="warning">
            Please Edit
          </Button>
        ) : displayStatus === "offline" ? (
          <Button className="btn-status-big" disabled color="danger">
            Offline
          </Button>
        ) : displayStatus === "re_delivery-delivery" ? (
          <Button className="btn-status-big" disabled color="warning">
            Re-delivery
          </Button>
        ) : displayStatus === "re_delivery-editing" ? (
          <Button className="btn-status-big" disabled color="warning">
            Re-delivery Editing
          </Button>
        ) : (
          <Button className="btn-status" disabled>Unknown Status</Button>
        )}
      </>
    );
  };

  const isQuickActionsLegendRow = (row) => row.isQuickActionsLegend === true;

  function rowHighlightStyle(row) {
    if (isQuickActionsLegendRow(row)) {
      return "quick-actions-legend-row";
    }
    if (hasQCIssues(row)) {
      return "qc-issues-alert";
    }
    return "";
  }

  const releaseInfoFormatter = (cell, row) => {
    if (isQuickActionsLegendRow(row)) {
      return null;
    }
    const artworkSrc = pickArtworkSrc(row.artwork);
    return (
      <div className="flexContainer releaseInfoCell">
        {artworkSrc && isWlDemoBlobRef(artworkSrc) ? (
          <ReleaseAvatarBlob raw={artworkSrc} alt={cell} />
        ) : artworkSrc ? (
          <img className="rounded-circle releaseInfoCover" src={artworkSrc} alt={cell} />
        ) : null}
        <div className="releaseInfoText">
          <p className="fontWeight700">{cell}</p>
          {row.catalogue_number && (
            <span className="releaseInfoCatTag">{row.catalogue_number}</span>
          )}
        </div>
      </div>
    );
  };

  const artistFormatter = (cell, row) => {
    if (isQuickActionsLegendRow(row)) {
      return null;
    }
    return (
        <p className="fontWeight700">{cell}</p>
    );
  };

  const actionFormatter = (cell, row) => {
    if (isQuickActionsLegendRow(row)) {
      return null;
    }
    return (
      <ReleaseDropdown
        {...row}
        action={dropdownAction}
        todUrl={todUrl}
        touUrl={touUrl}
        ppUrl={ppUrl}
        canAddTakedown={effectiveCanAddTakedown}
        release_admin_approval={release_admin_approval}
        is_premium_user={is_premium_user}
        is_sub_user={is_sub_user}
        is_employee_account={is_employee_account}
        preScanEnabled={acr_deep_scan}
      ></ReleaseDropdown>
    );
  };

  const listenTracksEnabled = release_admin_approval && (is_employee_account || is_premium_user);
  const preScanEnabled = release_admin_approval && acr_deep_scan && (is_employee_account || is_premium_user);

  const buildQuickActions = (row, options = {}) => {
    const quickActions = [
      { key: "store-urls", label: "Store Urls", content: <ReleaseStoreUrls {...row} /> },
      { key: "qc", label: "QC", content: <QCNote {...row} noteType={"Release"} /> },
    ];

    if (preScanEnabled) {
      quickActions.push({
        key: "pre-scan",
        label: "Pre Scan",
        content: (
          <PreScan
            {...row}
            release_admin_approval={release_admin_approval}
            action={{
              setPreScanSuccess,
              setPreScanError,
              setPreScanDeclarationSignSuccess,
              setPreScanDeclarationSignError,
              forceUpdate,
              setForceUpdate,
            }}
          />
        ),
      });
    }

    if (listenTracksEnabled) {
      quickActions.push({
        key: "listen",
        label: "Listen",
        content: (
          <ListenTracks
            {...row}
            preScanEnabled={acr_deep_scan}
            setForceUpdate={setForceUpdate}
            compact={options.compact}
          />
        ),
      });
    }

    quickActions.push(
      {
        key: "alert",
        label: "Alert",
        content: (
          <ACRAlert
            {...row}
            action={{ setDeclarationSignError, setDeclarationSignSuccess, forceUpdate, setForceUpdate }}
          />
        ),
      },
      { key: "eta", label: "ETA", content: <ReleaseEDT {...row} /> },
    );

    return quickActions;
  };

  const quickActionCount = 4 + (preScanEnabled ? 1 : 0) + (listenTracksEnabled ? 1 : 0);
  const quickActionsColumnWidth = `${quickActionCount * 64 + 40}px`;

  const getQuickActionLabels = () => {
    const labels = [
      { key: "store-urls", label: "Store Urls" },
      { key: "qc", label: "QC" },
    ];

    if (preScanEnabled) {
      labels.push({ key: "pre-scan", label: "Pre Scan" });
    }

    if (listenTracksEnabled) {
      labels.push({ key: "listen", label: "Listen" });
    }

    labels.push(
      { key: "alert", label: "Alert" },
      { key: "eta", label: "ETA" },
    );

    return labels;
  };

  const quickActionsFormatter = (cell, row) => {
    if (isQuickActionsLegendRow(row)) {
      return (
        <div className="quickActionsColumn quickActionsLegendRow">
          {getQuickActionLabels().map(({ key, label }) => (
            <div key={key} className="quickActionItem">
              <span className="quickActionLabel">{label}</span>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="quickActionsColumn">
        {buildQuickActions(row, { compact: true }).map(({ key, content }) => (
          <div key={key} className="quickActionItem">
            <div className="quickActionIcon">{content}</div>
          </div>
        ))}
      </div>
    );
  };

  const displayTableData = tableData.length > 0
    ? [{ id: QUICK_ACTIONS_LEGEND_ROW_ID, isQuickActionsLegend: true }, ...tableData]
    : tableData;

  const cardSortValue = `${searchQuery.ordering.name}:${searchQuery.ordering.order}`;
  const selectedCardSort =
    CARD_SORT_SELECT_OPTIONS.find((opt) => opt.value === cardSortValue) ?? CARD_SORT_SELECT_OPTIONS[0];

  const handleCardSortChange = (selected) => {
    if (!selected) return;
    const [name, order] = selected.value.split(":");
    searchQuery.setOrdering({ order, name, sort: name });
  };

  const selectedStatusFilter =
    STATUS_FILTER_SELECT_OPTIONS.find((opt) => opt.value === (searchQuery.status ?? ""))
    ?? STATUS_FILTER_SELECT_OPTIONS[0];

  const handleStatusFilterChange = (selected) => {
    if (!selected || !searchQuery.setStatus) return;
    searchQuery.setStatus(selected.value);
  };

  const renderStatusFilter = () => (
    <div className="release-toolbar-status-filter">
      <div className="release-toolbar-select-inner">
        <Select
          className="release-status-select"
          classNamePrefix="release-status"
          components={{ IndicatorSeparator: () => null }}
          options={STATUS_FILTER_SELECT_OPTIONS}
          value={selectedStatusFilter}
          onChange={handleStatusFilterChange}
          styles={RELEASE_TOOLBAR_SORT_SELECT_STYLES}
          isSearchable={false}
          aria-label="Filter by status"
        />
      </div>
    </div>
  );

  const hasActiveFilters =
    Boolean(searchQuery.query)
    || Boolean(subUserData.subUser)
    || Boolean(searchQuery.status)
    || searchQuery.ordering.order !== DEFAULT_RELEASE_ORDERING.order
    || searchQuery.ordering.name !== DEFAULT_RELEASE_ORDERING.name;

  const handleResetAllFilters = () => {
    searchQuery.setQuery("");
    subUserData.setSubUser("");
    subUserData.setSubUserEndpoint("");
    subUserData.setParentUserOnly("");
    if (searchQuery.setStatus) {
      searchQuery.setStatus("");
    }
    searchQuery.setOrdering(DEFAULT_RELEASE_ORDERING);
  };

  const renderFiltersReset = () =>
    hasActiveFilters ? (
      <Button
        className="btn btn-outline-info release-toolbar-filters-reset"
        onClick={handleResetAllFilters}
      >
        Reset
      </Button>
    ) : null;

  const userContext = { username, is_premium_user, is_sub_user, is_employee_account };
  const tenantContext = { release_admin_approval, acr_deep_scan };
  const actionContext = {
    dropdownAction,
    todUrl,
    touUrl,
    ppUrl,
    canAddTakedown: effectiveCanAddTakedown,
  };

  const renderLayoutToggle = () => {
    if (!layoutToggleEnabled) {
      return null;
    }
    return (
      <div className="release-layout-toggle" role="group" aria-label="Layout">
        {LAYOUT_OPTIONS.map(({ value, label }) => (
          <button
            key={value}
            type="button"
            className={`release-layout-btn${layoutView === value ? " active" : ""}`}
            onClick={() => handleLayoutChange(value)}
            aria-label={label}
            title={label}
          >
            <i
              className={`fas ${value === "table" ? "fa-th-list" : "fa-th-large"}`}
              aria-hidden="true"
            />
          </button>
        ))}
      </div>
    );
  };

  const columns = [
    {
      dataField: "name",
      text: "Release",
      formatter: releaseInfoFormatter,
      sort: true,
      classes: "release-col-info",
      headerClasses: "release-col-info",
      style: { width: "46%", minWidth: "460px" },
      headerStyle: { width: "46%", minWidth: "460px" },
    },
    {
      dataField: "name",
      hidden: true,
      csvExport: false,
    },
    {
      dataField: "get_artists",
      text: "Artist",
      sort: false,
      formatter: artistFormatter,
      classes: "release-col-artist",
      headerClasses: "release-col-artist",
      style: { width: "26%", minWidth: "320px" },
      headerStyle: { width: "26%", minWidth: "320px" },
    },
    {
      dataField: "catalogue_number",
      text: "Cat. No.",
      hidden: true,
      sort: true,
      csvExport: true,
    },
    {
      dataField: "ean",
      text: "EAN/UPC",
      hidden: true,
      sort: false,
      csvExport: true,
    },
    {
      dataField: "official_date",
      text: "Release Date",
      hidden: true,
      sort: true,
      csvExport: true,
    },
    {
      dataField: "status",
      text: "Status",
      sort: true,
      formatter: statusFormatter,
      classes: "release-col-status",
      headerClasses: "release-col-status",
      style: { width: "160px", minWidth: "160px" },
      headerStyle: { width: "160px", minWidth: "160px" },
    },
    {
      dataField: "status",
      hidden: true,
      csvExport: false,
    },
    {
      dataField: "quick_actions",
      text: "Quick Actions",
      formatter: quickActionsFormatter,
      sort: false,
      csvExport: false,
      classes: "release-col-quick-actions",
      headerClasses: "release-col-quick-actions",
      style: { width: quickActionsColumnWidth, minWidth: quickActionsColumnWidth },
      headerStyle: { width: quickActionsColumnWidth, minWidth: quickActionsColumnWidth },
    },
    {
      dataField: "action",
      text: "Manage",
      formatter: actionFormatter,
      sort: false,
      csvExport: false,
      classes: "release-col-manage",
      headerClasses: "release-col-manage",
      style: { width: "110px", minWidth: "110px" },
      headerStyle: { width: "110px", minWidth: "110px" },
    }
  ];

  const handleTableChange = (type, { sortField, sortOrder, data }) => {
    const ascDesc = sortOrder === "desc" ? "-" : "";
    if (sortField === "name" || sortField === "status" || sortField === "official_date" || sortField === "catalogue_number") {
      searchQuery.setOrdering({order: ascDesc, name: sortField, sort: sortField});
    }
  }

  return (
    <>
      <ViewLayout title={"My releases list"} headerAction={layoutToggleEnabled ? renderLayoutToggle() : null}>
        <ToolkitProvider
          keyField="id"
          data={tableData}
          columns={columns}
          search={{
            searchFormatted: true
          }}
          exportCSV={ {
            fileName: 'release.csv',
          } }
        >
          {props => (
            <div>
              {layoutView === "cards" ? (
                <div className="release-toolbar release-toolbar--cards">
                  <div className="release-toolbar-filters">
                    <div className="customSearch">
                      <Input
                        placeholder="Search releases, artists, cat no..."
                        value={searchQuery.query}
                        onChange={(e) => searchQuery.setQuery(e.target.value)}
                      />
                    </div>
                    {currentUser.is_premium_user && (
                      <ReleaseSubUserFilter
                        subUserData={subUserData}
                        selectStyles={RELEASE_TOOLBAR_SUBUSER_SELECT_STYLES}
                      />
                    )}
                    {renderStatusFilter()}
                    <div className="release-toolbar-sort">
                      <div className="release-toolbar-select-inner">
                        <i className="fas fa-filter release-filter-icon" aria-hidden="true" />
                        <Select
                          className="release-sort-select"
                          classNamePrefix="release-sort"
                          components={{ IndicatorSeparator: () => null }}
                          options={CARD_SORT_SELECT_OPTIONS}
                          value={selectedCardSort}
                          onChange={handleCardSortChange}
                          styles={RELEASE_TOOLBAR_SORT_SELECT_STYLES}
                          isSearchable={false}
                          aria-label="Sort releases"
                        />
                      </div>
                    </div>
                    {renderFiltersReset()}
                  </div>
                  <div className="release-toolbar-actions">
                    <ExportCSVButton
                      className="btn btn-outline-info"
                      {...props.csvProps}
                    >
                      Export CSV
                    </ExportCSVButton>
                  </div>
                </div>
              ) : (
                <div className="release-toolbar release-toolbar--table">
                  <div className="release-toolbar-filters">
                    <div className="customSearch">
                      <Input
                        placeholder="Search releases, artists, cat no..."
                        value={searchQuery.query}
                        onChange={(e) => searchQuery.setQuery(e.target.value)}
                      />
                    </div>
                    {currentUser.is_premium_user && (
                      <ReleaseSubUserFilter
                        subUserData={subUserData}
                        selectStyles={RELEASE_TOOLBAR_SUBUSER_SELECT_STYLES}
                      />
                    )}
                    {renderStatusFilter()}
                    {renderFiltersReset()}
                  </div>
                  <div className="release-toolbar-actions">
                    <ExportCSVButton
                      className="btn btn-outline-info"
                      {...props.csvProps}
                    >
                      Export CSV
                    </ExportCSVButton>
                  </div>
                </div>
              )}

              {layoutView === "cards" ? (
                <ReleaseListLayoutTwo
                  tableData={tableData}
                  userContext={userContext}
                  tenantContext={tenantContext}
                  actionContext={actionContext}
                  buildQuickActions={(row) => buildQuickActions(row, { compact: true })}
                />
              ) : (
                <ReleaseListLayoutOne
                  baseProps={props.baseProps}
                  displayTableData={displayTableData}
                  defaultSorted={defaultSorted}
                  rowHighlightStyle={rowHighlightStyle}
                  onTableChange={handleTableChange}
                />
              )}
            </div>
          )}
        </ToolkitProvider>
      </ViewLayout>
      {successAction && (
        <Alert style={{ marginTop: 10, marginBottom: 10 }} color="success">
          Release updated!
        </Alert>
      )}
      {declarationSignSuccess && (
        <Alert style={{ marginTop: 10, marginBottom: 10 }} color="success">
          Declaration submitted for admin review.
        </Alert>
      )}
      {declarationSignError && (
        <Alert style={{ marginTop: 10, marginBottom: 10 }} color="error">
          Something went wrong while submitting declaration, please try again later.
        </Alert>
      )}
      {preScanDeclarationSignSuccess && (
        <Alert style={{ marginTop: 10, marginBottom: 10 }} color="success">
          Pre Scan declaration submitted successfully.
        </Alert>
      )}
      {preScanDeclarationSignError && (
        <Alert style={{ marginTop: 10, marginBottom: 10 }} color="error">
          Something went wrong while submitting declaration, please try again later.
        </Alert>
      )}
      {preScanSuccess && (
        <Alert style={{ marginTop: 10, marginBottom: 10 }} color="success">
          Release successfully submitted for scan.
        </Alert>
      )}
      {preScanError && (
        <Alert style={{ marginTop: 10, marginBottom: 10 }} color="error">
          Something went wrong while scanning the release, please try again later.
        </Alert>
      )}
    </>
  );
};

export default ReleaseTable;
